/**
 * Zod schemas for the `vidya-figure` fenced code block grammar.
 *
 * The LLM emits these as JSON inside ```vidya-figure ... ``` blocks; we parse
 * + validate + dispatch to the right child renderer (rough.js for geometry,
 * function-plot for plot). Invalid JSON or schema failures render a graceful
 * fallback instead of crashing the chat answer.
 *
 * Kept synced with the schema documented in
 *   backend/agents_custom/instructions/qa/math_addendum.py
 * Any change here MUST be mirrored in the addendum so the LLM keeps emitting
 * what we can render.
 */

import { z } from 'zod';

// ──────────────────────────────────────────────────────────────────────────
// Shared primitives
// ──────────────────────────────────────────────────────────────────────────

const Point2D = z.tuple([z.number(), z.number()]);

const HexOrNamedColor = z.string().min(1).max(32);

// ──────────────────────────────────────────────────────────────────────────
// kind = "geometry"  →  rough.js render path
// ──────────────────────────────────────────────────────────────────────────

const Viewport = z.object({
  xmin: z.number(),
  xmax: z.number(),
  ymin: z.number(),
  ymax: z.number(),
});

const PolygonShape = z.object({
  type: z.literal('polygon'),
  // Accept 1+ points (not 3+). The LLM has a strong training-data bias
  // toward `polygon` as the universal primitive — it keeps emitting
  // `polygon` with 1 point to mark a single coordinate, even when the
  // prompt documents the `point` shape. Rather than fail the whole
  // figure on a tiny mistake, the renderer dispatches by count:
  //   1 point  → render as a point (filled dot)
  //   2 points → render as a segment (line)
  //   3+ points → render as a real polygon
  // The prompt still teaches `point` and `segment` as the correct
  // primitives; this is a safety net.
  points: z.array(Point2D).min(1),
  stroke: HexOrNamedColor.optional(),
  fill: HexOrNamedColor.optional(),
});

const CircleShape = z.object({
  type: z.literal('circle'),
  center: Point2D,
  radius: z.number().positive(),
  stroke: HexOrNamedColor.optional(),
  fill: HexOrNamedColor.optional(),
});

const SectorShape = z.object({
  type: z.literal('sector'),
  center: Point2D,
  radius: z.number().positive(),
  start_angle_deg: z.number(),
  end_angle_deg: z.number(),
  stroke: HexOrNamedColor.optional(),
  fill: HexOrNamedColor.optional(),
});

const ArcShape = z.object({
  type: z.literal('arc'),
  center: Point2D,
  radius: z.number().positive(),
  start_angle_deg: z.number(),
  end_angle_deg: z.number(),
  stroke: HexOrNamedColor.optional(),
});

const EllipseShape = z.object({
  type: z.literal('ellipse'),
  center: Point2D,
  rx: z.number().positive(),
  ry: z.number().positive(),
  stroke: HexOrNamedColor.optional(),
  fill: HexOrNamedColor.optional(),
});

const SegmentShape = z.object({
  type: z.literal('segment'),
  p1: Point2D,
  p2: Point2D,
  style: z.enum(['solid', 'dashed']).optional().default('solid'),
  stroke: HexOrNamedColor.optional(),
});

// Directed segment from p1 (tail) to p2 (head). Renders as a line with
// a filled triangular arrowhead at p2. Used for force vectors,
// velocity / acceleration, light rays, electron flow, and any
// "this points from here to there" diagram element.
const ArrowShape = z.object({
  type: z.literal('arrow'),
  p1: Point2D,                                    // tail
  p2: Point2D,                                    // head (arrow points here)
  style: z.enum(['solid', 'dashed']).optional().default('solid'),
  head_size: z.number().positive().optional(),    // viewport units; default ~0.3
  stroke: HexOrNamedColor.optional(),
});

const RightAngleShape = z.object({
  type: z.literal('right_angle'),
  vertex: Point2D,
  p1: Point2D,
  p2: Point2D,
  size: z.number().positive().optional(),
});

const AngleMarkerShape = z.object({
  type: z.literal('angle_marker'),
  vertex: Point2D,
  p1: Point2D,
  p2: Point2D,
  label: z.string().optional(),
});

const LabelShape = z.object({
  type: z.literal('label'),
  at: Point2D,
  text: z.string(),
});

// Single coordinate point (dot with optional label). The LLM used to
// abuse `polygon` with one point for this — Zod rejected it. A dedicated
// `point` shape both renders correctly and gives the LLM a tool that
// matches the intent.
const PointShape = z.object({
  type: z.literal('point'),
  at: Point2D,
  radius: z.number().positive().optional(),  // viewport units; default ~0.15
  fill: HexOrNamedColor.optional(),
  stroke: HexOrNamedColor.optional(),
  label: z.string().optional(),              // optional inline label e.g. "A (3, -1)"
});

const LabelLatexShape = z.object({
  type: z.literal('label_latex'),
  at: Point2D,
  text: z.string(),
});

// 3D primitives — rendered as 2D axonometric projections
const Sphere3DShape = z.object({
  type: z.literal('sphere_3d'),
  center: Point2D,
  radius: z.number().positive(),
  show_equator: z.boolean().optional(),
});

const Cylinder3DShape = z.object({
  type: z.literal('cylinder_3d'),
  center: Point2D,
  radius: z.number().positive(),
  height: z.number().positive(),
});

const Cone3DShape = z.object({
  type: z.literal('cone_3d'),
  apex: Point2D,
  base_center: Point2D,
  base_radius: z.number().positive(),
});

const GeometryShape = z.discriminatedUnion('type', [
  PolygonShape,
  CircleShape,
  SectorShape,
  ArcShape,
  EllipseShape,
  SegmentShape,
  ArrowShape,
  RightAngleShape,
  AngleMarkerShape,
  LabelShape,
  LabelLatexShape,
  PointShape,
  Sphere3DShape,
  Cylinder3DShape,
  Cone3DShape,
]);

export const GeometrySpec = z.object({
  viewport: Viewport,
  shapes: z.array(GeometryShape).min(1),
});

// ──────────────────────────────────────────────────────────────────────────
// kind = "plot"  →  function-plot render path
// ──────────────────────────────────────────────────────────────────────────

const Axis = z.object({
  domain: z.tuple([z.number(), z.number()]),
  ticks: z.array(z.number()).optional(),
  label: z.string().optional(),
});

const FunctionDataItem = z.object({
  fn: z.string().min(1),
  color: HexOrNamedColor.optional(),
  title: z.string().optional(),
  attr: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
  nSamples: z.number().int().positive().optional(),
});

const ScatterDataItem = z.object({
  fnType: z.literal('points'),
  graphType: z.literal('scatter'),
  points: z.array(Point2D),
  color: HexOrNamedColor.optional(),
  attr: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});

const PlotDataItem = z.union([FunctionDataItem, ScatterDataItem]);

// ──────────────────────────────────────────────────────────────────────────
// Interactive parameter sliders (optional)
// ──────────────────────────────────────────────────────────────────────────
// Mirrors `ParamDef` in backend/app/services/figure_schema.py — kept in
// lock-step so a block that validates server-side also validates here.
// When present, PlotRenderer renders a `<input type="range">` per key
// and passes the current values into function-plot's per-datum `scope`,
// letting `fn` strings reference the param names directly. Backwards
// compatible: omit `params` for a static plot.

// Reserved names — anything that would shadow a math.js identifier used
// inside `fn` strings. Mirror of `_RESERVED_PARAM_NAMES` in figure_schema.py.
const RESERVED_PARAM_NAMES = new Set([
  'x', 'e', 'pi', 'tau', 'i',
  'sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2',
  'sec', 'csc', 'cot', 'sinh', 'cosh', 'tanh',
  'log', 'log2', 'log10', 'ln', 'exp', 'sqrt',
  'abs', 'floor', 'ceil', 'round', 'sign',
  'min', 'max', 'pow', 'mod',
  'Infinity', 'NaN',
]);

const PARAM_NAME_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,15}$/;
const MAX_PARAMS = 5;

const ParamDef = z
  .object({
    min: z.number(),
    max: z.number(),
    default: z.number(),
    step: z.number().positive().optional(),
    label: z.string().optional(),
  })
  .refine((p) => p.min < p.max, {
    message: '`min` must be less than `max`',
  })
  .refine((p) => p.min <= p.default && p.default <= p.max, {
    message: '`default` must satisfy min ≤ default ≤ max',
  });

const ParamsRecord = z
  .record(z.string(), ParamDef)
  .refine((params) => Object.keys(params).length <= MAX_PARAMS, {
    message: `params may declare at most ${MAX_PARAMS} keys`,
  })
  .refine(
    (params) => Object.keys(params).every((k) => PARAM_NAME_RE.test(k)),
    {
      message: 'param names must match /^[a-zA-Z][a-zA-Z0-9_]{0,15}$/',
    },
  )
  .refine(
    (params) => Object.keys(params).every((k) => !RESERVED_PARAM_NAMES.has(k)),
    {
      message: 'param name shadows a reserved math.js identifier',
    },
  );

// ──────────────────────────────────────────────────────────────────────────
// Interactive cursor (reference line, optional)
// ──────────────────────────────────────────────────────────────────────────
// Mirrors `Cursor` in backend/app/services/figure_schema.py. A vertical
// dashed line at the cursor's x value with a slider below and a per-curve
// y-readout. v1 supports axis="x" only — backend validator rejects "y".
const Cursor = z.object({
  axis: z.literal('x'),                       // v1: vertical cursors only
  default: z.number().optional(),             // defaults to xAxis midpoint
  step: z.number().positive().optional(),
  label: z.string().optional(),
});

export const PlotSpec = z
  .object({
    xAxis: Axis,
    yAxis: Axis,
    grid: z.boolean().optional(),
    data: z.array(PlotDataItem).min(1),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    params: ParamsRecord.optional(),
    cursor: Cursor.optional(),
  })
  .refine(
    (spec) => {
      // If cursor.default is set, it must lie within xAxis.domain.
      if (!spec.cursor || spec.cursor.default == null) return true;
      const [lo, hi] = spec.xAxis.domain;
      return spec.cursor.default >= lo && spec.cursor.default <= hi;
    },
    { message: 'cursor.default must lie within xAxis.domain' },
  );

// ──────────────────────────────────────────────────────────────────────────
// Top-level discriminated union over `kind`
// ──────────────────────────────────────────────────────────────────────────

export const VidyaFigureBlock = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('geometry'), spec: GeometrySpec }),
  z.object({ kind: z.literal('plot'), spec: PlotSpec }),
]);

// ──────────────────────────────────────────────────────────────────────────
// Inferred TypeScript types — re-export so renderers can consume directly
// ──────────────────────────────────────────────────────────────────────────

export type VidyaFigureBlockT = z.infer<typeof VidyaFigureBlock>;
export type GeometrySpecT = z.infer<typeof GeometrySpec>;
export type PlotSpecT = z.infer<typeof PlotSpec>;
export type GeometryShapeT = z.infer<typeof GeometryShape>;
export type PlotDataItemT = z.infer<typeof PlotDataItem>;
export type ParamDefT = z.infer<typeof ParamDef>;
export type CursorT = z.infer<typeof Cursor>;

/**
 * Parse a vidya-figure code-block body into a validated VidyaFigureBlockT.
 * Returns { ok: true, data } or { ok: false, error } — never throws.
 */
export function parseVidyaFigure(rawBody: string):
  | { ok: true; data: VidyaFigureBlockT }
  | { ok: false; error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawBody.trim());
  } catch (e) {
    return {
      ok: false,
      error: `Invalid JSON in vidya-figure block: ${(e as Error).message}`,
    };
  }
  const result = VidyaFigureBlock.safeParse(parsed);
  if (!result.success) {
    const issue = result.error.issues[0];
    const path = issue?.path?.join('.') || '(root)';
    return {
      ok: false,
      error: `vidya-figure validation failed at ${path}: ${issue?.message ?? 'unknown error'}`,
    };
  }
  return { ok: true, data: result.data };
}
